import { FormTab, TabbedForm } from "react-admin";
import {
  GroupTitle,
  Toolbar,
  TransactionLogsField,
  TransactionNotesInput,
  TransactionNotesIsPrivateInput,
  useSaveMutation,
} from "ra-friendsofbabba";
import React, { useMemo } from "react";
import {
  minLength,
  required,
  useGetIdentity,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from "ra-core";

import ActivitiesField from "./fields/ActivitiesField";
import CompanyInput from "./inputs/CompanyInput";
import DocsField from "./fields/DocsField";
import EquipmentsField from "./fields/EquipmentsField";
import Input from "../component/input/Input";
import LocalTextContentInput from "./inputs/TextContentInput";
import PatentsField from "./fields/PatentsField";
import { PrintButton } from "../component/button";
import ProductionChainInput from "./inputs/ProductionChainInput";
import ResearchAreaInput from "./inputs/ResearchAreaInput";
import StaffUsersField from "./fields/StaffUsersField";
import TextContentInput from "../component/input/TextContentInput";
import { get } from "lodash";

const minLengthOnSentValidator = (length) => (value, values) =>
  values.state === "sent" ? minLength(length)(value) : undefined;
const requiredOnSentValidator = () => (value, values) =>
  values.state === "sent" ? required()(value) : undefined;

const Form = ({ create = false, permissions, ...props }) => {
  const admin = permissions && permissions(["admin"]);
  const translate = useTranslate();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const notify = useNotify();
  const save = useSaveMutation({
    ...props,
    onSuccess: (response, values) => {
      const prevState = get(values, "transaction.state");
      const nextState = get(response, "data.state");
      const isMoved = prevState !== nextState;
      notify(
        `resources.research-projects.messages.${values.state}_${
          isMoved ? "moved" : "loop"
        }.save_success`
      );
      if (values.id > 0) {
        refresh();
      } else {
        redirect("edit", props.basePath, response.data.id);
      }
    },
  });
  const { identity } = useGetIdentity();
  const { id, ...company } = useMemo(() => {
    const company = get(identity, "company", { id: undefined });
    if (!company || company === null || admin) {
      return {};
    }
    return company;
  }, [identity, admin]);

  return (
    <TabbedForm
      {...props}
      redirect={false}
      initialValues={{
        is_private: false,
        state: "draft",
        company,
        partner: {
          company_type_id: 3,
        },
      }}
      toolbar={
        <Toolbar useWorkflow maxButtonsToDisplay={1}>
          <PrintButton resource="research-projects" />
          <PrintButton
            resource="research-projects"
            action="print-sent.pdf"
            label="resources.research-projects.buttons.print_sent"
          />
        </Toolbar>
      }
      save={save}
    >
      <FormTab label={"resources.research-projects.tabs.general"}>
        <TextContentInput
          source="title"
          minLengthValidatorCustom={minLengthOnSentValidator}
          maxLength={200}
          multiline
          fullWidth
          validate={[required()]}
        />
        <Input
          source="company"
          fullWidth
          component={
            <CompanyInput
              showTitle
              requiredValidator={requiredOnSentValidator}
            />
          }
        />
        <Input
          source="partner"
          fullWidth
          component={
            <CompanyInput
              showTitle
              showCompanyTypeSelector={false}
              requiredValidator={requiredOnSentValidator}
              type="private"
            />
          }
        />

        <TransactionLogsField source="logs" admin={admin} fullWidth />
        <TransactionNotesInput
          source="notes"
          helperText="resources.transactions.fields.notes.help"
          fullWidth
        />
        <TransactionNotesIsPrivateInput
          admin={admin}
          source="is_private"
          helperText="resources.transactions.fields.is_private.help"
          fullWidth
        />
      </FormTab>
      <FormTab label={"resources.research-projects.tabs.section_1"}>
        <GroupTitle
          title={translate("resources.research-projects.sections.1")}
        />

        <Input
          source="research_area_id_root"
          component={<ResearchAreaInput level={1} />}
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
        <Input
          source="research_area_id"
          component={
            <ResearchAreaInput level={2} dependency="research_area_id_root" />
          }
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
        <Input
          source="production_chain_id"
          component={<ProductionChainInput />}
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
      </FormTab>
      <FormTab label={"resources.research-projects.tabs.section_2a"}>
        <GroupTitle
          title={translate("resources.research-projects.sections.a1")}
        />
        <TextContentInput
          source="description"
          minLength={500}
          minLengthValidatorCustom={minLengthOnSentValidator}
          maxLength={5000}
          multiline
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
        <Input
          source="activities_list"
          component={<ActivitiesField />}
          fullWidth
        />
        <GroupTitle
          title={translate("resources.research-projects.sections.a2")}
        />
        <TextContentInput
          source="problem_and_solution"
          minLength={500}
          minLengthValidatorCustom={minLengthOnSentValidator}
          maxLength={1000}
          multiline
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
        <TextContentInput
          source="coherence"
          minLength={500}
          minLengthValidatorCustom={minLengthOnSentValidator}
          maxLength={2000}
          multiline
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
        <TextContentInput
          source="activities"
          minLength={500}
          minLengthValidatorCustom={minLengthOnSentValidator}
          maxLength={2000}
          multiline
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
        <GroupTitle
          title={translate("resources.research-projects.sections.a3")}
        />
        <LocalTextContentInput
          source="motivations"
          minLength={500}
          minLengthValidatorCustom={minLengthOnSentValidator}
          maxLength={2000}
          multiline
          fullWidth
          validate={[requiredOnSentValidator()]}
        />
      </FormTab>
      <FormTab label={"resources.research-projects.tabs.section_2b"}>
        <GroupTitle
          title={translate("resources.research-projects.sections.b1")}
        />

        <Input
          source="public_equipments"
          type="public"
          fullWidth
          component={<EquipmentsField />}
        />
        <Input
          source="private_equipments"
          type="private"
          fullWidth
          component={<EquipmentsField />}
        />
        <Input
          source="public_staff_users"
          type="public"
          fullWidth
          component={<StaffUsersField />}
        />
        <Input
          source="private_staff_users"
          type="private"
          fullWidth
          component={<StaffUsersField />}
        />
        <Input source="patents" fullWidth component={<PatentsField />} />
      </FormTab>
      <FormTab label={"resources.research-projects.tabs.section_docs"}>
        <Input source="docs" fullWidth component={<DocsField />} />
      </FormTab>
    </TabbedForm>
  );
};
export default Form;
